// Task #3822 — dettaglio di un Progetto del PA nel drawer: istruzioni,
// membri, knowledge, skill, conversazioni e artefatti del progetto.
// Drill-in dal ProjectsPanel; layout compatto pensato per la colonna stretta
// (drawer desktop) e per l'overlay full-screen mobile.
import React, { useState } from 'react';
import {
  ChevronLeft, Layers, Loader2, LogOut, MessageSquare, Pencil, Plus, Trash2, X,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { formatRelativeTime } from '../chatFormat';
import { projectsApi, usePaProjectDetail } from './useProjects';
import { MembersSection, KnowledgeSection, SkillsSection } from './ProjectSections';
import { ProjectConversationViewer } from './ProjectConversationViewer';

export interface ProjectDetailPanelProps {
  projectId: string;
  currentUserId: string | null;
  onBack: () => void;
  /** Progetto eliminato o abbandonato: torna alla lista. */
  onClosed: () => void;
  onSelectConversation: (id: string) => void;
  onNewProjectConversation?: (projectId: string) => Promise<void> | void;
}

export const ProjectDetailPanel: React.FC<ProjectDetailPanelProps> = ({
  projectId, currentUserId, onBack, onClosed, onSelectConversation, onNewProjectConversation,
}) => {
  const { detail, conversations, artifacts, loading, error, refresh } = usePaProjectDetail(projectId);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editInstructions, setEditInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [viewerConvId, setViewerConvId] = useState<string | null>(null);

  const isOwner = detail?.myRole === 'owner';

  const startEdit = () => {
    if (!detail) return;
    setEditName(detail.name);
    setEditDescription(detail.description ?? '');
    setEditInstructions(detail.instructions ?? '');
    setEditing(true);
    setActionError(null);
  };

  const handleSave = async () => {
    const name = editName.trim();
    if (!name || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const updated = await projectsApi.update(projectId, {
        name,
        description: editDescription.trim() || null,
        instructions: editInstructions.trim() || null,
      });
      if (updated) { setEditing(false); await refresh(); }
      else setActionError('Salvataggio non riuscito');
    } catch (err) {
      console.error('[ProjectDetailPanel] update failed', err);
      setActionError('Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      window.setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setDeleting(true);
    setActionError(null);
    try {
      const ok = await projectsApi.remove(projectId);
      if (ok) onClosed();
      else setActionError('Eliminazione non riuscita');
    } catch (err) {
      console.error('[ProjectDetailPanel] delete failed', err);
      setActionError('Eliminazione non riuscita');
    } finally {
      setDeleting(false);
    }
  };

  const handleNewChat = async () => {
    if (!onNewProjectConversation || creatingChat) return;
    setCreatingChat(true);
    setActionError(null);
    try {
      await onNewProjectConversation(projectId);
    } catch (err) {
      console.error('[ProjectDetailPanel] new chat failed', err);
      setActionError('Creazione conversazione non riuscita');
    } finally {
      setCreatingChat(false);
    }
  };

  if (viewerConvId) {
    const conv = conversations.find((c) => c.id === viewerConvId) ?? null;
    return (
      <ProjectConversationViewer
        projectId={projectId}
        conversationId={viewerConvId}
        conversationTitle={conv?.title ?? null}
        authorName={conv?.userName ?? null}
        onBack={() => setViewerConvId(null)}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="px-2 py-2 flex items-center gap-1.5 bg-card border-b border-border shrink-0">
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onBack} title="Tutti i progetti">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-xs font-semibold truncate" title={detail?.name}>{detail?.name ?? 'Progetto'}</div>
          {detail && (
            <div className="text-[10px] text-muted-foreground truncate">
              {detail.myRole === 'owner' ? 'proprietario' : 'membro'}
              {detail.ownerName && detail.myRole !== 'owner' ? ` · di ${detail.ownerName}` : ''}
            </div>
          )}
        </div>
        {isOwner && !editing && (
          <>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={startEdit} title="Modifica progetto">
              <Pencil className="h-3 w-3" />
            </Button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => void handleDelete()}
              title={confirmDelete ? 'Conferma eliminazione' : 'Elimina progetto'}
              aria-label={confirmDelete ? 'Conferma eliminazione' : 'Elimina progetto'}
              className={`h-6 min-w-[24px] px-1 rounded border text-[10px] inline-flex items-center justify-center gap-1 transition ${
                confirmDelete
                  ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                  : 'bg-card border-border text-muted-foreground hover:bg-red-50 hover:border-red-300 hover:text-red-600'
              }`}
            >
              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              {confirmDelete && <span>Conferma</span>}
            </button>
          </>
        )}
        {editing && (
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => setEditing(false)}>
            <X className="h-3 w-3 mr-0.5" /> Annulla
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="px-2 py-2 space-y-3">
          {loading && !detail && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}
          {actionError && <div className="text-xs text-red-600">{actionError}</div>}

          {detail && editing && (
            <div className="space-y-1.5">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={200}
                placeholder="Nome del progetto"
                className="w-full h-7 px-2 rounded border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
              />
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={2}
                placeholder="Descrizione"
                className="w-full px-2 py-1 rounded border border-border bg-background text-xs resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
              />
              <textarea
                value={editInstructions}
                onChange={(e) => setEditInstructions(e.target.value)}
                rows={5}
                placeholder="Istruzioni del progetto: ereditate da ogni conversazione al suo interno"
                className="w-full px-2 py-1 rounded border border-border bg-background text-xs resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
              />
              <Button
                size="sm"
                className="h-7 w-full text-xs bg-violet-600 hover:bg-violet-700 text-white"
                disabled={!editName.trim() || saving}
                onClick={() => void handleSave()}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Salva'}
              </Button>
            </div>
          )}

          {detail && !editing && (
            <>
              {detail.description && (
                <p className="text-[11px] text-muted-foreground whitespace-pre-wrap px-1">{detail.description}</p>
              )}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1">Istruzioni</div>
                {detail.instructions ? (
                  <p className="text-[11px] text-foreground whitespace-pre-wrap px-1.5 py-1 bg-muted rounded max-h-32 overflow-y-auto">
                    {detail.instructions}
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground px-1.5 py-1">
                    Nessuna istruzione: le chat del progetto non ereditano indicazioni aggiuntive.
                  </p>
                )}
              </div>

              {/* Conversazioni del progetto */}
              <div>
                <div className="flex items-center justify-between px-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Conversazioni ({conversations.length})
                  </span>
                  {onNewProjectConversation && (
                    <Button
                      variant="ghost" size="sm" className="h-5 w-5 p-0"
                      disabled={creatingChat}
                      onClick={() => void handleNewChat()}
                      title="Nuova chat nel progetto" aria-label="Nuova chat nel progetto"
                    >
                      {creatingChat ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    </Button>
                  )}
                </div>
                <ul className="mt-0.5">
                  {conversations.length === 0 && (
                    <li className="text-[10px] text-muted-foreground px-1.5 py-1">
                      Nessuna conversazione: aprine una per lavorare col contesto del progetto.
                    </li>
                  )}
                  {conversations.map((c) => {
                    const mine = c.userId === currentUserId;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => (mine ? onSelectConversation(c.id) : setViewerConvId(c.id))}
                          className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] text-left hover:bg-muted"
                          title={mine ? 'Apri in chat' : `Sola lettura · di ${c.userName ?? 'altro membro'}`}
                        >
                          <MessageSquare className={`h-3 w-3 shrink-0 ${mine ? 'text-violet-500' : 'text-muted-foreground'}`} />
                          <span className="flex-1 min-w-0">
                            <span className="block truncate">{c.title || 'Senza titolo'}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">
                              {mine ? 'tua' : (c.userName ?? 'altro membro')}
                              {c.lastMessageAt ? ` · ${formatRelativeTime(c.lastMessageAt)}` : ''}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Artefatti del progetto */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                  Artefatti ({artifacts.length})
                </div>
                <ul className="mt-0.5">
                  {artifacts.length === 0 && (
                    <li className="text-[10px] text-muted-foreground px-1.5 py-1">Nessun artefatto nel progetto.</li>
                  )}
                  {artifacts.map((a) => (
                    <li key={a.id} className="flex items-center gap-1.5 px-1.5 py-1 text-[11px]">
                      <Layers className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="flex-1 min-w-0 truncate" title={a.title}>{a.title}</span>
                      <span className="text-[9px] text-muted-foreground shrink-0">{a.kind} · v{a.version}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <MembersSection
                projectId={projectId}
                members={detail.members}
                isOwner={isOwner}
                currentUserId={currentUserId}
                onChanged={() => void refresh()}
                onLeft={onClosed}
              />

              <KnowledgeSection
                projectId={projectId}
                knowledgeAgentId={detail.knowledgeAgentId}
                docs={detail.knowledgeDocs}
                isOwner={isOwner}
                onChanged={() => void refresh()}
              />

              <SkillsSection
                projectId={projectId}
                skills={detail.skills}
                isOwner={isOwner}
                onChanged={() => void refresh()}
              />

              {!isOwner && (
                <div className="pt-1 border-t border-border">
                  <p className="text-[9px] text-muted-foreground px-1 pb-1">
                    Nel progetto il tuo PA usa i TUOI permessi: il progetto condivide contesto, mai poteri.
                  </p>
                  <div className="text-[10px] text-muted-foreground px-1 flex items-center gap-1">
                    <LogOut className="h-3 w-3" />
                    Per uscire dal progetto usa la ✕ accanto al tuo nome fra i membri.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
